import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Container,
    Typography,
    Divider,
    TextField,
    Autocomplete,
    Chip,
    InputAdornment,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    IconButton,
} from "@mui/material";

// import { useAuth } from "../../helpers/AuthProvider"; // useAuth not used in this component
import queryGraphql from "../../helpers/ApiInterface";
import { ExpandMore } from "@mui/icons-material";
import MainDrawer from "../MainDrawer";
import AIAgentRequestsTable from "./AIAgentRequestsTable"; // Changed import
import MiniBot from "../MiniBot";

interface SearchOptions {
    chipLabel: string;
    dropdownLabel: string;
    category: string;
    groupBy: string;
}

let searchOptions: SearchOptions[] = [];

const AIAgentRequestsPage: React.FC = () => {
    const [aiAgents, setAIAgents] = useState<any[]>([]); // State variable for AI Agent data
    const [searchQuery, setSearchQuery] = useState<SearchOptions[]>([]);
    const [updates, setUpdates] =  useState<any[]>([]);
    const navigate = useNavigate();
    React.useEffect(() =>{
        const fetchData = async () => {
            try {
                const data = await queryGraphql(`
                        query Query {
                        resourceStatuses {
                            id
                            currentStatus
                            resourceId
                        }
                        }
                    `);
                if(data.resourceStatuses){
                    setUpdates(data.resourceStatuses);
                }
                }
                catch(e){
            console.log(e);
                }
    }
    fetchData();
    },[]);  
    
    React.useEffect(() => {
        const fetchData = async () => {
            try {
                // Updated GraphQL Query to include cloudProvider
                const data = await queryGraphql(`
                        query GetSolutionAIAgents {
                          getSolutionAIAgents {
                            id
                            name
                            solutionType
                            cloudProvider
                            environment
                            status
                            details {
                              aiAgent {
                                projectId
                                region
                                imageUrl
                                gpu
                                modelName
                                modelTemperature
                                modelMaxTokens
                                modelStreaming
                                repoUrl
                              }
                            }
                            reasonForRejection
                            createdAt
                            updatedAt
                            solutionOwner {
                              name
                              googleProfilePicture
                            }
                            application {
                              applicationName
                              id
                            }
                            solutionApprover {
                              id
                              name
                            }
                          }
                        }
                    `);

                // Using getSolutionAIAgents from the response
                const rawAgentsData = data.data?.getSolutionAIAgents || data.getSolutionAIAgents || [];

                let aiAgentsArr = rawAgentsData.map((e: any) => {
                    return {
                        // Mapped fields from new query
                        id: e.id,
                        agentName: e.name,
                        applicationName: e.application?.applicationName,
                        status: e.status ? e.status.toLowerCase() : 'pending',
                        createdAt: e.createdAt ? new Date(Number(e.createdAt)).toISOString() : null,
                        actionedAt: e.updatedAt ? new Date(Number(e.updatedAt)).toISOString() : null,
                        
                        // Owner details from solutionOwner
                        aIAgentOwnerName: e.solutionOwner?.name,
                        aIAgentOwnerAvatar: e.solutionOwner?.googleProfilePicture,
                        
                        cloudProvider: e.cloudProvider, // Added cloudProvider

                        // Details from nested aiAgent object
                        projectId: e.details?.aiAgent?.projectId,
                        region: e.details?.aiAgent?.region,
                        environment: e.environment,
                        imageUrl: e.details?.aiAgent?.imageUrl,
                        gpu: e.details?.aiAgent?.gpu,
                        modelName: e.details?.aiAgent?.modelName,
                        repoUrl: e.details?.aiAgent?.repoUrl,
                        
                        // Approver and rejection reason
                        aIAgentApproverName: e.solutionApprover?.name, // Mapped from solutionApprover.name
                        reasonForRejection: e.reasonForRejection,
                    };
                });
                setAIAgents(aiAgentsArr);

                searchOptions = aiAgentsArr
                    .map((e: any) => {
                        const options = [
                            {
                                category: "agentName",
                                groupBy: "AGENT NAME",
                                chipLabel: `Agent Name: ${e.agentName}`,
                                dropdownLabel: e.agentName,
                            },
                            {
                                category: "aIAgentOwnerName",
                                groupBy: "OWNER",
                                chipLabel: `Owner: ${e.aIAgentOwnerName}`,
                                dropdownLabel: e.aIAgentOwnerName,
                            },
                            {
                                category: "aIAgentApproverName",
                                groupBy: "APPROVER",
                                chipLabel: `Approver: ${e.aIAgentApproverName}`,
                                dropdownLabel: e.aIAgentApproverName,
                            },
                        ];
                        if (e.applicationName) {
                             options.push({
                                category: "applicationName",
                                groupBy: "APPLICATION NAME",
                                chipLabel: `Application: ${e.applicationName}`,
                                dropdownLabel: e.applicationName,
                            });
                        }
                        if (e.cloudProvider) {
                            options.push({
                               category: "cloudProvider",
                               groupBy: "CLOUD PROVIDER",
                               chipLabel: `Provider: ${e.cloudProvider}`,
                               dropdownLabel: e.cloudProvider,
                           });
                       }
                        if (e.status) {
                            options.push({
                                category: "status",
                                groupBy: "STATUS",
                                chipLabel: `Status: ${e.status.charAt(0).toUpperCase() + e.status.slice(1)}`,
                                dropdownLabel: e.status.charAt(0).toUpperCase() + e.status.slice(1),
                            });
                        }
                        return options;
                    })
                    .flat();

                searchOptions = searchOptions.filter(
                    (obj, index, self) =>
                        obj.dropdownLabel &&
                        index ===
                        self.findIndex(
                            (t) => t.category === obj.category && t.dropdownLabel === obj.dropdownLabel
                        )
                );
                searchOptions.sort((a, b) => {
                    if (a.groupBy > b.groupBy) return 1;
                    if (a.groupBy < b.groupBy) return -1;
                    if (a.dropdownLabel > b.dropdownLabel) return 1;
                    if (a.dropdownLabel < b.dropdownLabel) return -1;
                    return 0;
                });

            } catch (error) {
                console.log(error);
            }
        };

        fetchData();
    }, []);

    const filteredAIAgents =
        searchQuery.length === 0
            ? aiAgents
            : aiAgents.filter(
                    (agent: any) =>
                        (searchQuery.filter((e) => e.category === "agentName").length > 0
                            ? searchQuery
                                    .filter((e) => e.category === "agentName")
                                    .map((e) => e.dropdownLabel?.toLowerCase())
                                    .includes(agent.agentName?.toLowerCase())
                            : true) &&
                        (searchQuery.filter((e) => e.category === "aIAgentOwnerName").length > 0
                            ? searchQuery
                                    .filter((e) => e.category === "aIAgentOwnerName")
                                    .map((e) => e.dropdownLabel?.toLowerCase())
                                    .includes(agent.aIAgentOwnerName?.toLowerCase())
                            : true) &&
                         (searchQuery.filter((e) => e.category === "aIAgentApproverName").length > 0
                            ? searchQuery
                                    .filter((e) => e.category === "aIAgentApproverName")
                                    .map((e) => e.dropdownLabel?.toLowerCase())
                                    .includes(agent.aIAgentApproverName?.toLowerCase())
                            : true) &&
                        (searchQuery.filter((e) => e.category === "applicationName").length > 0
                            ? searchQuery
                                    .filter((e) => e.category === "applicationName")
                                    .map((e) => e.dropdownLabel?.toLowerCase())
                                    .includes(agent.applicationName?.toLowerCase())
                            : true) &&
                        (searchQuery.filter((e) => e.category === "cloudProvider").length > 0
                            ? searchQuery
                                    .filter((e) => e.category === "cloudProvider")
                                    .map((e) => e.dropdownLabel?.toLowerCase())
                                    .includes(agent.cloudProvider?.toLowerCase())
                            : true) &&
                        (searchQuery.filter((e) => e.category === "status").length > 0
                            ? searchQuery
                                    .filter((e) => e.category === "status")
                                    .map((e) => e.dropdownLabel?.toLowerCase())
                                    .includes(agent.status?.toLowerCase())
                            : true)
              );
    
    const filteredActiveAIAgents = filteredAIAgents.filter(
        (agent: any) => agent.status === "active" || agent.status === "approved"
    );
    const filteredPendingAIAgents = filteredAIAgents.filter(
        (agent: any) => agent.status === "pending"
    );
    const filteredRejectedAIAgents = filteredAIAgents.filter(
        (agent: any) => agent.status === "rejected"
    );

    return (
        <Box sx={{ display: "flex" }}>
            <MainDrawer index={"requests-aiagent"} />
            <MiniBot/>
            <Box
                component="main"
                sx={{
                    flex: 1,
                    bgcolor: "#fafafa",
                    minHeight: window.innerHeight,
                }}
            >
                <Container maxWidth={false} disableGutters>
                    <Box bgcolor="#FFFFFF" p={2} mb="18px">
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginY: 1,
                                marginLeft: 2,
                            }}
                        >
                            <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold">
                                Requests
                            </Typography>
                            <IconButton
                                aria-label="Help" 
                                onClick={()=> window.open(`${process.env.REACT_APP_HOST_DOMAIN}/docs` , "_blank")}
                                sx={{
                                  padding: 0, 
                                  border: 'none',  
                                  mr:2,
                                  '&:hover': {
                                    
                                    backgroundColor: 'action.hover', 
                                  },
                                  
                                }}
                              >
                                <Box
                                  component="img"
                                  src="/assets/Help.svg" 
                                  alt="" 
                                  sx={{
                                    width: 40,  
                                    height: 40,
                                    display: 'block', 
                                  }}
                                />
                              </IconButton>
                        </Box>
                        <Divider sx={{ marginY: 1 }} />
                        <Box
                            sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mx: 2 }}
                        >
                            <Box>
                                <Typography
                                    fontFamily="Gilroy-SemiBold"
                                    fontSize="16px"
                                >{`AI AGENTS (${aiAgents.length})`}</Typography>
                                <Typography
                                    fontFamily="Gilroy-Regular"
                                    fontSize="14px"
                                >{`View all the requests raised for AI Agents`}</Typography>
                            </Box>
                            <Autocomplete
                                multiple
                                options={searchOptions}
                                filterSelectedOptions
                                getOptionLabel={(option) => option.dropdownLabel}
                                defaultValue={[]}
                                groupBy={(option) => option.groupBy}
                                renderTags={(tagValue, getTagProps) => {
                                    return tagValue.map((option, index) => (
                                        <Chip {...getTagProps({ index })} size="small" label={option.chipLabel} />
                                    ));
                                }}
                                onChange={(e, newVal, reason) => {
                                    setSearchQuery(newVal);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="filled"
                                        placeholder="Search by agent name, owner, status etc"
                                        fullWidth
                                        size="small"
                                        hiddenLabel
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <InputAdornment position="start">
                                                        <img
                                                            src="/assets/search-normal.svg"
                                                            alt="Filter Icon"
                                                            style={{ width: "16px", paddingLeft: "4px" }}
                                                        />
                                                    </InputAdornment>
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                            disableUnderline: true,
                                            style: {
                                                backgroundColor: "#f4f4f4",
                                                minHeight: "45px",
                                                width: "500px",
                                                borderRadius: "8px",
                                                fontFamily: "Gilroy-Regular",
                                                fontSize: "14px",
                                            },
                                        }}
                                    />
                                )}
                            />
                        </Box>
                    </Box>
                    <Box m={2}>
                        <Accordion
                            defaultExpanded={filteredPendingAIAgents.length > 0 ? true : false}
                            elevation={0}
                            sx={{
                                borderRadius: "8px",
                                mb: "16px",
                                "&:before": {
                                    display: "none",
                                },
                                bgcolor: "#fafafa",
                            }}
                            disabled={filteredPendingAIAgents.length === 0}
                        >
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <img
                                        src="/assets/clock.svg"
                                        alt="Pending Icon"
                                        style={{ width: "20px", marginRight: "8px" }}
                                    />
                                    <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                        Pending AI Agents ({filteredPendingAIAgents.length})
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                {filteredPendingAIAgents.length > 0 && (
                                    <AIAgentRequestsTable aiAgents={filteredPendingAIAgents} resourceStatuses={updates}/>
                                )}
                            </AccordionDetails>
                        </Accordion>
                        <Accordion
                        defaultExpanded={true}
                            elevation={0}
                            sx={{
                                borderRadius: "8px",
                                mb: "16px",
                                "&:before": {
                                    display: "none",
                                },
                                bgcolor: "#fafafa",
                            }}
                            disabled={filteredActiveAIAgents.length === 0}
                        >
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <img
                                        src="/assets/tickcircle.svg"
                                        alt="Active Icon"
                                        style={{ width: "20px", marginRight: "8px" }}
                                    />
                                    <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                        Active AI Agents ({filteredActiveAIAgents.length})
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                {filteredActiveAIAgents.length > 0 && (
                                    <AIAgentRequestsTable aiAgents={filteredActiveAIAgents} resourceStatuses={updates}/>
                                )}
                            </AccordionDetails>
                        </Accordion>

                        <Accordion
                            defaultExpanded={filteredRejectedAIAgents.length > 0 ? true : false}
                            elevation={0}
                            sx={{
                                borderRadius: "8px",
                                "&:before": {
                                    display: "none",
                                },
                                bgcolor: "#fafafa",
                            }}
                            disabled={filteredRejectedAIAgents.length === 0}
                        >
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <img
                                        src="/assets/reject.svg"
                                        alt="Rejected Icon"
                                        style={{ width: "20px", marginRight: "8px" }}
                                    />
                                    <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                        Rejected AI Agents ({filteredRejectedAIAgents.length})
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                {filteredRejectedAIAgents.length > 0 && (
                                    <AIAgentRequestsTable aiAgents={filteredRejectedAIAgents} resourceStatuses={updates}/>
                                )}
                            </AccordionDetails>
                        </Accordion>
                    </Box>
                </Container>
            </Box>
        </Box>
    );
};

export default AIAgentRequestsPage;

// import React, { useState } from "react";
// import { useNavigate } from "react-router-dom";
// import {
//     Box,
//     Container,
//     Typography,
//     Divider,
//     TextField,
//     Autocomplete,
//     Chip,
//     InputAdornment,
//     Accordion,
//     AccordionSummary,
//     AccordionDetails,
// } from "@mui/material";

// import queryGraphql from "../../helpers/ApiInterface";
// import { ExpandMore } from "@mui/icons-material";
// import MainDrawer from "../MainDrawer";
// import AIAgentRequestsTable from "./AIAgentRequestsTable"; // Changed import
// import MiniBot from "../MiniBot";

// interface SearchOptions {
//     chipLabel: string;
//     dropdownLabel: string;
//     category: string;
//     groupBy: string;
// }

// let searchOptions: SearchOptions[] = [];

// const AIAgentRequestsPage: React.FC = () => {
//     const [aiAgents, setAIAgents] = useState<any[]>([]); // Changed state variable name
//     const [searchQuery, setSearchQuery] = useState<SearchOptions[]>([]);
//     const [updates, setUpdates] =  useState<any[]>([]);

//     React.useEffect(() =>{
//         const fetchData = async () => {
//             try {
//                 const data = await queryGraphql(`
//                         query Query {
//                         resourceStatuses {
//                             id
//                             currentStatus
//                             resourceId
//                         }
//                         }
//                     `);
//                 if(data.resourceStatuses){
//                     setUpdates(data.resourceStatuses);
//                 }
//                 }
//                 catch(e){
//             console.log(e);
//                 }
//     }
//     fetchData();
//     },[]);  
    
//     React.useEffect(() => {
//         const fetchData = async () => {
//             try {
//                 const data = await queryGraphql(`
//                         query GetAIAgents {
//                           getAIAgents {
//                             id
//                             agentName
//                             aIAgentOwner {
//                               id
//                               name
//                               googleProfilePicture
//                               email
//                             }
//                             status
//                             environment
//                             projectId
//                             serviceName
//                             region
//                             imageUrl
//                             gpu
//                             applicationName
//                             modelName
//                             modelTemperature
//                             modelMaxTokens
//                             modelStreaming
//                             updatedAt
//                             createdAt
//                             repoUrl
//                             reasonForRejection
//                             aIAgentApprover {
//                               id
//                               name
//                               email
//                             }
//                           }
//                         }
//                     `);

//                 let aiAgentsArr = data.getAIAgents.map((e: any) => { // Changed to getAIAgents
//                     return {
//                         ...e,
//                         // Flattened properties for easier access and consistency
//                         aIAgentOwnerAvatar: e?.aIAgentOwner?.googleProfilePicture,
//                         aIAgentOwnerName: e?.aIAgentOwner?.name,
//                         aIAgentApproverName: e?.aIAgentApprover?.name,
//                         // aIAgentApproverAvatar: e?.aIAgentApprover?.googleProfilePicture, // Not available in API for AI Agent Approver
//                         createdAt: e.createdAt ? new Date(Number(e.createdAt)).toISOString() : null, // Handle potential nulls and ensure conversion if it's a numeric string
//                         actionedAt: e.updatedAt ? new Date(Number(e.updatedAt)).toISOString() : null, // Mapped from updatedAt
//                     };
//                 });
//                 setAIAgents(aiAgentsArr);

//                 searchOptions = aiAgentsArr
//                     .map((e: any) => {
//                         return [
//                             {
//                                 category: "agentName", // Changed category
//                                 groupBy: "AGENT NAME", // Changed groupBy
//                                 chipLabel: `Agent Name: ${e.agentName}`, // Changed label
//                                 dropdownLabel: e.agentName, // Changed label
//                             },
//                             {
//                                 category: "aIAgentOwnerName", // Changed category
//                                 groupBy: "OWNER",
//                                 chipLabel: `Owner: ${e.aIAgentOwnerName}`, // Changed label
//                                 dropdownLabel: e.aIAgentOwnerName, // Changed label
//                             },
//                             {
//                                 category: "aIAgentApproverName", // Changed category
//                                 groupBy: "ACTIONED BY",
//                                 chipLabel: `Actioned By: ${e.aIAgentApproverName}`, // Changed label
//                                 dropdownLabel: e.aIAgentApproverName, // Changed label
//                             },
//                             // Add other searchable fields if needed, e.g., environment
//                             // {
//                             //     category: "environment",
//                             //     groupBy: "ENVIRONMENT",
//                             //     chipLabel: `Environment: ${e.environment}`,
//                             //     dropdownLabel: e.environment,
//                             // },
//                         ];
//                     })
//                     .flat();

//                 searchOptions = searchOptions.filter(
//                     (obj, index, self) =>
//                         index ===
//                         self.findIndex(
//                             (t) => t.category === obj.category && t.dropdownLabel === obj.dropdownLabel && obj.dropdownLabel // Ensure dropdownLabel is not null/undefined
//                         )
//                 );
//                 searchOptions.sort((a, b) => {
//                     if (a.groupBy > b.groupBy) return 1;
//                     if (a.groupBy < b.groupBy) return -1;
//                     if (a.dropdownLabel > b.dropdownLabel) return 1;
//                     if (a.dropdownLabel < b.dropdownLabel) return -1;
//                     return 0;
//                 });

//             } catch (error) {
//                 console.log(error);
//             }
//         };

//         fetchData();
//     }, []);

//     const filteredAIAgents = // Changed variable name
//         searchQuery.length === 0
//             ? aiAgents
//             : aiAgents.filter(
//                     (agent: any) => // Changed parameter name
//                         (searchQuery.filter((e) => e.category === "agentName").length > 0
//                             ? searchQuery
//                                     .filter((e) => e.category === "agentName")
//                                     .map((e) => e.dropdownLabel?.toLowerCase())
//                                     .includes(agent.agentName?.toLowerCase())
//                             : true) &&
//                         (searchQuery.filter((e) => e.category === "aIAgentOwnerName").length > 0
//                             ? searchQuery
//                                     .filter((e) => e.category === "aIAgentOwnerName")
//                                     .map((e) => e.dropdownLabel?.toLowerCase())
//                                     .includes(agent.aIAgentOwnerName?.toLowerCase())
//                             : true) &&
//                         (searchQuery.filter((e) => e.category === "aIAgentApproverName").length > 0
//                             ? searchQuery
//                                     .filter((e) => e.category === "aIAgentApproverName")
//                                     .map((e) => e.dropdownLabel?.toLowerCase())
//                                     .includes(agent.aIAgentApproverName?.toLowerCase())
//                             : true)
//                         // Add filtering for other categories if added to searchOptions
//                         // && (searchQuery.filter((e) => e.category == "environment").length > 0
//                         //     ? searchQuery
//                         //             .filter((e) => e.category == "environment")
//                         //             .map((e) => e.dropdownLabel.toLowerCase())
//                         //             .includes(agent.environment?.toLowerCase())
//                         //     : true)
//               );

//     const filteredActiveAIAgents = filteredAIAgents.filter( // Changed variable name
//         (agent: any) => agent.status === "active" || agent.status === "approved" // Consider common active statuses
//     );
//     const filteredPendingAIAgents = filteredAIAgents.filter( // Changed variable name
//         (agent: any) => agent.status === "pending"
//     );
//     const filteredRejectedAIAgents = filteredAIAgents.filter( // Changed variable name
//         (agent: any) => agent.status === "rejected"
//     );

//     return (
//         <Box sx={{ display: "flex" }}>
//             <MainDrawer index={"requests-aiagent"} /> {/* Changed index */}
//             <MiniBot/>
//             <Box
//                 component="main"
//                 sx={{
//                     flex: 1,
//                     bgcolor: "#fafafa",
//                     minHeight: window.innerHeight,
//                 }}
//             >
//                 <Container maxWidth={false} disableGutters>
//                     <Box bgcolor="#FFFFFF" p={2} mb="18px">
//                         <Box
//                             sx={{
//                                 display: "flex",
//                                 justifyContent: "flex-start",
//                                 alignItems: "center",
//                                 marginY: 1,
//                                 marginLeft: 2,
//                             }}
//                         >
//                             <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold">
//                                 Requests
//                             </Typography>
//                         </Box>
//                         <Divider sx={{ marginY: 1 }} />
//                         <Box
//                             sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mx: 2 }}
//                         >
//                             <Box>
//                                 <Typography
//                                     fontFamily="Gilroy-SemiBold"
//                                     fontSize="16px"
//                                 >{`AI AGENTS (${aiAgents.length})`}</Typography> {/* Changed text */}
//                                 <Typography
//                                     fontFamily="Gilroy-Regular"
//                                     fontSize="14px"
//                                 >{`View all the requests raised for AI Agents`}</Typography> {/* Changed text */}
//                             </Box>
//                             <Autocomplete
//                                 multiple
//                                 options={searchOptions}
//                                 filterSelectedOptions
//                                 getOptionLabel={(option) => option.dropdownLabel}
//                                 defaultValue={[]}
//                                 groupBy={(option) => option.groupBy}
//                                 renderTags={(tagValue, getTagProps) => {
//                                     return tagValue.map((option, index) => (
//                                         <Chip {...getTagProps({ index })} size="small" label={option.chipLabel} />
//                                     ));
//                                 }}
//                                 onChange={(e, newVal, reason) => {
//                                     setSearchQuery(newVal);
//                                 }}
//                                 renderInput={(params) => (
//                                     <TextField
//                                         {...params}
//                                         variant="filled"
//                                         placeholder="Search by agent name, owner, approver etc" // Changed placeholder
//                                         fullWidth
//                                         size="small"
//                                         hiddenLabel
//                                         InputProps={{
//                                             ...params.InputProps,
//                                             startAdornment: (
//                                                 <>
//                                                     <InputAdornment position="start">
//                                                         <img
//                                                             src="/assets/search-normal.svg"
//                                                             alt="Filter Icon"
//                                                             style={{ width: "16px", paddingLeft: "4px" }}
//                                                         />
//                                                     </InputAdornment>
//                                                     {params.InputProps.startAdornment}
//                                                 </>
//                                             ),
//                                             disableUnderline: true,
//                                             style: {
//                                                 backgroundColor: "#f4f4f4",
//                                                 minHeight: "45px",
//                                                 width: "500px",
//                                                 borderRadius: "8px",
//                                                 fontFamily: "Gilroy-Regular",
//                                                 fontSize: "14px",
//                                             },
//                                         }}
//                                     />
//                                 )}
//                             />
//                         </Box>
//                     </Box>
//                     <Box m={2}>
//                         <Accordion
//                             defaultExpanded={filteredPendingAIAgents.length > 0 ? true : false}
//                             elevation={0}
//                             sx={{
//                                 borderRadius: "8px",
//                                 mb: "16px",
//                                 "&:before": {
//                                     display: "none",
//                                 },
//                                 bgcolor: "#fafafa",
//                             }}
//                             disabled={filteredPendingAIAgents.length === 0}
//                         >
//                             <AccordionSummary expandIcon={<ExpandMore />}>
//                                 <Box sx={{ display: "flex", alignItems: "center" }}>
//                                     <img
//                                         src="/assets/clock.svg"
//                                         alt="Pending Icon"
//                                         style={{ width: "20px", marginRight: "8px" }}
//                                     />
//                                     <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
//                                         Pending AI Agents ({filteredPendingAIAgents.length}) {/* Changed text */}
//                                     </Typography>
//                                 </Box>
//                             </AccordionSummary>
//                             <AccordionDetails>
//                                 {filteredPendingAIAgents.length > 0 && (
//                                     <AIAgentRequestsTable aiAgents={filteredPendingAIAgents} resourceStatuses={updates}/> // Changed prop name and component
//                                 )}
//                             </AccordionDetails>
//                         </Accordion>
//                         <Accordion
//                         defaultExpanded={true}
//                             elevation={0}
//                             sx={{
//                                 borderRadius: "8px",
//                                 mb: "16px",
//                                 "&:before": {
//                                     display: "none",
//                                 },
//                                 bgcolor: "#fafafa",
//                             }}
//                             disabled={filteredActiveAIAgents.length === 0}
//                         >
//                             <AccordionSummary expandIcon={<ExpandMore />}>
//                                 <Box sx={{ display: "flex", alignItems: "center" }}>
//                                     <img
//                                         src="/assets/tickcircle.svg"
//                                         alt="Active Icon"
//                                         style={{ width: "20px", marginRight: "8px" }}
//                                     />
//                                     <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
//                                         Active AI Agents ({filteredActiveAIAgents.length}) {/* Changed text */}
//                                     </Typography>
//                                 </Box>
//                             </AccordionSummary>
//                             <AccordionDetails>
//                                 {filteredActiveAIAgents.length > 0 && (
//                                     <AIAgentRequestsTable aiAgents={filteredActiveAIAgents} resourceStatuses={updates} /> // Changed prop name and component
//                                 )}
//                             </AccordionDetails>
//                         </Accordion>

//                         <Accordion
//                             elevation={0}
//                             sx={{
//                                 borderRadius: "8px",
//                                 "&:before": {
//                                     display: "none",
//                                 },
//                                 bgcolor: "#fafafa",
//                             }}
//                             disabled={filteredRejectedAIAgents.length === 0}
//                         >
//                             <AccordionSummary expandIcon={<ExpandMore />}>
//                                 <Box sx={{ display: "flex", alignItems: "center" }}>
//                                     <img
//                                         src="/assets/reject.svg"
//                                         alt="Rejected Icon"
//                                         style={{ width: "20px", marginRight: "8px" }}
//                                     />
//                                     <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
//                                         Rejected AI Agents ({filteredRejectedAIAgents.length}) {/* Changed text */}
//                                     </Typography>
//                                 </Box>
//                             </AccordionSummary>
//                             <AccordionDetails>
//                                 {filteredRejectedAIAgents.length > 0 && (
//                                     <AIAgentRequestsTable aiAgents={filteredRejectedAIAgents} resourceStatuses={updates}/> // Changed prop name and component
//                                 )}
//                             </AccordionDetails>
//                         </Accordion>
//                     </Box>
//                 </Container>
//             </Box>
//         </Box>
//     );
// };

// export default AIAgentRequestsPage;